import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { scrollToSection } from "./Navbar";

const HERO_IMG =
  "https://images.unsplash.com/photo-1527138332241-b3eed4958d40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxuZXclMjB6ZWFsYW5kJTIwcm9sbGluZyUyMGdyZWVuJTIwaGlsbHMlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzkzNzQzMDYyfDA&ixlib=rb-4.1.0&q=85";

const STATS = [
  { value: "EST. 2026", label: "Aotea / Kāwhia, Aotearoa" },
  { value: "GACP · GMP", label: "Pharmaceutical-grade focus" },
  { value: "Kaupapa Māori", label: "Tikanga-led governance" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden bg-[#121512]" data-testid="hero-section">
      <motion.div style={{ y: imgY }} className="absolute inset-0 will-change-transform">
        <div
          className="absolute inset-0 bg-cover bg-center animate-hero-drift"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-[#121512]/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#121512] via-[#121512]/25 to-[#121512]/30" />

      <motion.div style={{ y: contentY, opacity: fade }} className="relative z-10 max-w-[1440px] mx-auto w-full px-6 sm:px-10 pb-14 sm:pb-20 pt-40">
        <h1 className="font-serif-brand font-medium text-[#FBF9F4] text-[13.5vw] sm:text-7xl lg:text-[6.2rem] leading-[1.02] tracking-tight max-w-5xl" data-testid="hero-headline">
          <MaskedLine delay={0.25}>Kaupapa Māori</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-[#D4A359]">medicinal cannabis</span>
          </MaskedLine>
          <MaskedLine delay={0.55}>cultivation &amp; research.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-xl text-base sm:text-lg text-[#FBF9F4]/75 leading-relaxed font-light"
          data-testid="hero-subcopy"
        >
          Pharmaceutical-grade product and distinctive cultivars from the volcanic soils of
          Kāwhia — grown with data-backed provenance, creating pathways for local rangatahi.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToSection("#contact")}
            data-testid="hero-cta-partnership"
            className="group flex items-center gap-2.5 rounded-full bg-[#D4A359] px-7 py-3.5 text-sm font-semibold text-[#121512] transition-colors duration-300 hover:bg-[#FBF9F4]"
          >
            Explore Partnership
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => scrollToSection("#kaupapa")}
            data-testid="hero-cta-kaupapa"
            className="rounded-full border border-[#FBF9F4]/35 px-7 py-3.5 text-sm font-medium text-[#FBF9F4] transition-colors duration-300 hover:border-[#D4A359] hover:text-[#D4A359]"
          >
            Our Kaupapa
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-14 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-[#FBF9F4]/15 pt-7"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.value}>
              <p className="font-serif-brand text-xl sm:text-2xl text-[#FBF9F4]">{s.value}</p>
              <p className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#A3B899] mt-1.5">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 right-8 z-10 hidden sm:flex flex-col items-center gap-2 text-[#FBF9F4]/60"
      >
        <span className="font-mono-brand text-[9px] uppercase tracking-[0.3em] [writing-mode:vertical-rl]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-cue-drop" />
      </motion.div>
    </section>
  );
};
